import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL, IPayrollCalendarExtendedAggregatorResponseDto, IPlanLiteDto } from '@/dtos';

import {
  PayrollCalendarFormFields,
  PayrollCalendarFrequency,
  PayrollCalendarFrequencyResponse,
  PayrollCalendarObject,
  PayrollCalendarParams,
  PayrollCalendarPayrollGroupResponse,
  PayrollCalendarPlan,
} from '../payrollCalendar.types';

const calendarDataFormatter = (data?: IPayrollCalendarExtendedAggregatorResponseDto) => {
  if (!data) return undefined;

  const formattedPlans = (data?.calendar_plans as unknown as IPlanLiteDto[])?.map((plan) => ({
    id: `${plan.id}`,
    planCode: plan.plan_code,
  }));

  const formattedNonPlans = (data?.non_calendar_plans as unknown as IPlanLiteDto[])?.map((nonPlan) => ({
    id: `${nonPlan.id}`,
    planCode: nonPlan.plan_code,
  }));

  const formattedConfig = {
    calendarId: `${data.id}`,
    [PayrollCalendarFormFields.CALENDAR_NAME]: data.name,
    [PayrollCalendarFormFields.FREQUENCY]:
      PayrollCalendarFrequency[data?.config?.frequency as keyof typeof PayrollCalendarFrequency],
    [PayrollCalendarFormFields.START_DATE]: dayjs(data?.config?.start_date).format(DEFAULT_DATE_FORMAT),
    ...(data?.config?.day_of_month
      ? { [PayrollCalendarFormFields.DAY_OF_MONTH]: data.config.day_of_month }
      : {}),
    ...((data?.config?.frequency as unknown as PayrollCalendarFrequencyResponse) ===
    PayrollCalendarFrequencyResponse.MONTHLY
      ? { [PayrollCalendarFormFields.DAY_OF_MONTH]: data?.config?.first_day_of_month }
      : {}),
    ...(data?.config?.day_of_week
      ? { [PayrollCalendarFormFields.DAY_OF_WEEK]: data.config.day_of_week }
      : {}),
    ...((data?.config?.frequency as unknown as PayrollCalendarFrequencyResponse) ===
    PayrollCalendarFrequencyResponse.BI_WEEKLY
      ? { [PayrollCalendarFormFields.BI_WEEKLY_DAY_OF_WEEK]: data?.config?.day_of_week }
      : {}),
    ...(data?.config?.first_day_of_month
      ? { [PayrollCalendarFormFields.SEMI_MONTHLY_DAY_OF_MONTH_1]: data.config.first_day_of_month }
      : {}),
    ...(data?.config?.second_day_of_month
      ? { [PayrollCalendarFormFields.SEMI_MONTHLY_DAY_OF_MONTH_2]: data.config.second_day_of_month }
      : {}),
    ...(data?.config?.month
      ? { [PayrollCalendarFormFields.DATE_OF_YEAR]: dayjs(data?.schedule?.[0]).format(DEFAULT_DATE_FORMAT) }
      : {}),
  };

  const formattedData: PayrollCalendarObject = {
    id: `${data.id}`,
    name: data.name || '',
    frequencyType: data.frequency_type || '',
    createdAt: data.created_at as unknown as string,
    startDate: data.start_date as unknown as string,
    endDate: data.end_date as unknown as string,
    status: data.status || '',
    organizationId: data.organization_id || 0,
    organizationPath: data.organization_path || '',
    nextAutoprolongAt: data.next_autoprolong_at as unknown as string,
    config: formattedConfig as PayrollCalendarParams,
    groupType: data.group_type || '',
    schedule: data.schedule as unknown as string[],
    calendarPlans: formattedPlans as PayrollCalendarPlan[],
    nonCalendarPlans: formattedNonPlans as PayrollCalendarPlan[],
    calendarPayrollGroups: data.calendar_payroll_groups as unknown as PayrollCalendarPayrollGroupResponse[],
    nonCalendarPayrollGroups:
      data.non_calendar_payroll_groups as unknown as PayrollCalendarPayrollGroupResponse[],
  };

  return formattedData;
};
export const QUERY_KEY = 'useGetPayrollCalendarByIdQuery';

export const useGetPayrollCalendarByIdQuery = (calendarId?: string) => {
  const { isLoading, error, data, isSuccess, isError, refetch } = useQuery(
    [QUERY_KEY, calendarId],
    () => api.get(API_URL.UI_PAYROLL_CALENDARS_BY_ID(calendarId || '')),
    {
      enabled: !!calendarId,
      cacheTime: 0,
    }
  );

  const updatedData = useMemo(() => data?.data, [data]);
  const formattedData = useMemo(() => calendarDataFormatter(updatedData), [updatedData]);

  return {
    isLoading,
    error,
    isSuccess,
    isError,
    refetch,
    data: formattedData,
  };
};
